import React, { useState, useCallback, useRef } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Platform, ActivityIndicator, Alert } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import theme from '../../../../config/theme';
import { useRouter, useFocusEffect } from 'expo-router';
import { databaseService } from '../../../../services/databaseService';
import { APP_CONSTANTS } from '../../../../config/constants';
import { observer } from '@legendapp/state/react';
import { useWebAlert } from '../../../../components/WebAlert';
import { globalState } from '../../../../state/globalState';
import { realtimeTrigger } from '@/state/realtime';
import UpgradePrompt from '@/components/subscription/UpgradePrompt';
import {
  getSettingsContentPaddingBottom,
  getSettingsHeaderPaddingTop,
} from '@/utils/safeArea';
import { hasPremiumAccess } from '@/utils/subscriptionAccess';
import {
  getUserTypeLabel,
  isAdminUserType,
  isDeveloperUserType,
  isMotoristaUserType,
} from '@/utils/userRoles';

// Interface para dados de usuário
interface User {
  id: string;
  name: string;
  email: string;
  telefone?: string;
  empresa_id?: string | null;
  user_type: string;
  created_at: string;
}

// Tela de administração de usuários
// Aqui o admin poderá cadastrar e gerenciar usuários do sistema
function UsersScreen() {
  const router = useRouter();
  const safeAreaInsets = useSafeAreaInsets();
  const headerPaddingTop = getSettingsHeaderPaddingTop(safeAreaInsets.top);
  const scrollViewBottomPadding = getSettingsContentPaddingBottom(safeAreaInsets.bottom);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const loadRequestRef = useRef(0);
  const realtimeCounter = realtimeTrigger.counter.get();
  const { showAlert } = useWebAlert();
  const subscriptionSnapshot = globalState.subscription.get();
  const profileSnapshot = globalState.user.profile.get();
  const currentEmpresaId = profileSnapshot?.empresa_id || globalState.company.data.get()?.id || null;
  const premiumAccess = hasPremiumAccess(subscriptionSnapshot, profileSnapshot);

  // Função para mostrar alerta adaptável à plataforma
  const showMessage = (title: string, msg: string, onOk?: () => void) => {
    if (Platform.OS !== 'android') {
      // No web e no iOS, usar o alerta temático do app
      showAlert(title, msg, [{ text: 'OK', onPress: onOk }]);
    } else {
      // No Android, manter o Alert nativo
      Alert.alert(title, msg, [{ text: 'OK', onPress: onOk }]);
    }
  };

  // Função para carregar usuários
  const loadUsers = useCallback(async () => {
    if (!premiumAccess) return;
    const request = ++loadRequestRef.current;
    try {
      setIsLoading(true);
      setError(null);

      if (!currentEmpresaId) {
        setUsers([]);
        setError('Empresa do administrador não encontrada.');
        return;
      }
      
      // Buscar apenas usuários da empresa atual para evitar vazamento de cache local.
      const usersData = await databaseService.listItems<User>(
        APP_CONSTANTS.DATABASE.TABLES.USERS.NAME,
        { empresa_id: currentEmpresaId }
      );
      
      if (request === loadRequestRef.current) setUsers(usersData);
    } catch (err: any) {
      if (request !== loadRequestRef.current) return;
      console.error('Erro ao carregar usuários:', err);
      setError('Não foi possível carregar os usuários. Tente novamente.');
    } finally {
      if (request === loadRequestRef.current) setIsLoading(false);
    }
  }, [currentEmpresaId, premiumAccess]);

  // Carregar usuários sempre que a tela receber foco
  useFocusEffect(
    useCallback(() => {
      if (!premiumAccess) {
        return;
      }
      loadUsers();
      return () => { loadRequestRef.current += 1; };
    }, [loadUsers, premiumAccess, realtimeCounter])
  );

  // Função para navegar para tela de cadastro de novo usuário
  const handleAddUser = () => {
    router.push('/settings/users/new');
  };

  // Função para voltar
  const handleGoBack = () => {
    router.back();
  };

  if (!premiumAccess) {
    return (
      <View style={[styles.container, styles.restrictedWrapper]}>
        <View style={[styles.header, { paddingTop: headerPaddingTop }]}>
          <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
            <MaterialCommunityIcons name="arrow-left" size={24} color={theme.colors.text.inverse} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Usuários</Text>
          <View style={{ width: 40 }} />
        </View>
        <UpgradePrompt variant="restricted" />
      </View>
    );
  }

  // Função para navegar para detalhes do usuário
  const handleViewUserDetails = (userId: string) => {
    router.push(`/settings/users/${userId}`);
  };

  // Renderiza item de usuário na lista
  const renderUserItem = (user: User) => {
    return (
      <View key={user.id} style={styles.userItem}>
        <View style={styles.userIconContainer}>
          <MaterialCommunityIcons 
            name="account-circle" 
            size={40} 
            color={theme.colors.primary.default} 
          />
        </View>
        <View style={styles.userInfo}>
          <Text style={styles.userName}>{user.name}</Text>
          <Text style={styles.userEmail}>{user.email}</Text>
          <View style={styles.userTypeContainer}>
            <Text style={styles.userType}>
              {getUserTypeLabel(user.user_type)}
            </Text>
          </View>
        </View>
        <TouchableOpacity 
          style={styles.optionsButton}
          onPress={() => handleViewUserDetails(user.id)}
        >
          <MaterialCommunityIcons name="dots-vertical" size={20} color={theme.colors.text.tertiary} />
        </TouchableOpacity>
      </View>
    );
  };

  // Renderiza o conteúdo da lista
  const renderContent = () => {
    if (isLoading) {
      return (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary.default} />
          <Text style={styles.loadingText}>Carregando usuários...</Text>
        </View>
      );
    }

    if (error) {
      return (
        <View style={styles.centerContainer}>
          <MaterialCommunityIcons name="alert-circle-outline" size={48} color={theme.colors.util.error} />
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity style={styles.retryButton} onPress={loadUsers}>
            <Text style={styles.retryButtonText}>Tentar novamente</Text>
          </TouchableOpacity>
        </View>
      );
    }

    if (users.length === 0) {
      return (
        <View style={styles.emptyState}>
          <MaterialCommunityIcons name="account-group-outline" size={48} color={theme.colors.text.tertiary} />
          <Text style={styles.emptyStateText}>Nenhum usuário cadastrado ainda.</Text>
        </View>
      );
    }

    // Agrupamento e ordenação
    const visibleUsers = users.filter(u => !isDeveloperUserType(u.user_type));

    if (visibleUsers.length === 0) {
      return (
        <View style={styles.emptyState}>
          <MaterialCommunityIcons name="account-group-outline" size={48} color={theme.colors.text.tertiary} />
          <Text style={styles.emptyStateText}>Nenhum usuário gerenciável encontrado para esta empresa.</Text>
        </View>
      );
    }

    const admins = visibleUsers.filter(u => isAdminUserType(u.user_type)).sort((a, b) => a.name.localeCompare(b.name));
    const motoristas = visibleUsers.filter(u => isMotoristaUserType(u.user_type)).sort((a, b) => a.name.localeCompare(b.name));
    const legados = visibleUsers
      .filter(u => !isAdminUserType(u.user_type) && !isMotoristaUserType(u.user_type))
      .sort((a, b) => a.name.localeCompare(b.name));

    return (
      <View style={styles.listContainer}>
        {admins.length > 0 && (
          <View style={{ marginBottom: 16 }}>
            <Text style={styles.groupTitle}>Administrador</Text>
            {admins.map(renderUserItem)}
          </View>
        )}
        {motoristas.length > 0 && (
          <View style={{ marginBottom: 16 }}>
            <Text style={styles.groupTitle}>Motorista</Text>
            {motoristas.map(renderUserItem)}
          </View>
        )}
        {legados.length > 0 && (
          <View>
            <Text style={styles.groupTitle}>Perfis Legados</Text>
            {legados.map(renderUserItem)}
          </View>
        )}
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <View style={[styles.header, { paddingTop: headerPaddingTop }]}>
        <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
          <MaterialCommunityIcons name="arrow-left" size={24} color={theme.colors.text.inverse} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Usuários</Text>
        <View style={{ width: 40 }} />
      </View>
      <ScrollView contentContainerStyle={[styles.scrollViewContent, { paddingBottom: scrollViewBottomPadding }]}>
        {renderContent()}
      </ScrollView>
      {/* FAB para adicionar novo usuário */}
      <TouchableOpacity style={styles.fab} onPress={handleAddUser} activeOpacity={0.8}>
        <MaterialCommunityIcons name="account-plus" size={28} color={theme.colors.text.inverse} />
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary,
  },
  restrictedWrapper: {
    justifyContent: 'flex-start',
  },
  header: {
    backgroundColor: theme.colors.primary.default,
    padding: theme.spacing.lg,
    paddingTop: Platform.OS === 'ios' ? theme.spacing.xl + theme.spacing.sm : theme.spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    flex: 1,
    textAlign: 'center',
    fontSize: theme.fontSizes.xl,
    fontWeight: theme.fontWeights.bold as any,
    color: theme.colors.text.inverse,
  },
  scrollViewContent: {
    flexGrow: 1,
    padding: theme.spacing.lg,
  },
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 60,
  },
  emptyStateText: {
    color: theme.colors.text.tertiary,
    fontSize: theme.fontSizes.md,
    marginTop: 12,
    textAlign: 'center',
  },
  fab: {
    position: 'absolute',
    right: 24,
    bottom: 32,
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: theme.colors.primary.default,
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
  },
  centerContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 60,
  },
  loadingText: {
    marginTop: 16,
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.secondary,
  },
  errorText: {
    marginTop: 16,
    fontSize: theme.fontSizes.md,
    color: theme.colors.util.error,
    textAlign: 'center',
    marginHorizontal: 20,
  },
  retryButton: {
    marginTop: 20,
    paddingVertical: 10,
    paddingHorizontal: 16,
    backgroundColor: theme.colors.primary.default,
    borderRadius: theme.borderRadius.md,
  },
  retryButtonText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.medium as any,
  },
  listContainer: {
    width: '100%',
  },
  userItem: {
    flexDirection: 'row',
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.md,
    alignItems: 'center',
    ...theme.shadows.sm,
  },
  userIconContainer: {
    marginRight: theme.spacing.md,
  },
  userInfo: {
    flex: 1,
  },
  userName: {
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.semibold as any,
    color: theme.colors.text.primary,
    marginBottom: 2,
  },
  userEmail: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
    marginBottom: 4,
  },
  userTypeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  userType: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.primary.default,
    backgroundColor: `${theme.colors.primary.default}20`,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
  },
  optionsButton: {
    padding: 8,
  },
  groupTitle: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
    fontWeight: theme.fontWeights.semibold as any,
    marginBottom: 4,
    marginLeft: 4,
    letterSpacing: 0.5,
    opacity: 0.7,
  },
});

// Aplicar o HOC observer após definir o componente
const ObservedUsersScreen = observer(UsersScreen);

export default ObservedUsersScreen; 
